import request from "../../../util/SuperagentUtils";
import type { Response } from "superagent";
import type { Account } from '../../../types/Account';
import type { LocationFeatureDetailsByName } from '../../../types/Location';
import { hasAdminAccess } from "../../../util/AccountUtils";
import { securSpacePlatforms } from "../../../components/constants/securspace-constants";

const LOCAL_TESTING_MODE = false; // Set to true to enable local testing mode

const FILE_NAMES = {
  STANDARD: "locations.json",
  ADMIN: "all_locations.json",
} as const;

const CLOUDFRONT_CDN_URL = "https://static.secur.space/data/";

// Relative path proxied by the Vite dev server (see vite.config.mts) to avoid
// CORS errors when fetching the CDN JSON directly from `localhost` in dev.
const LOCAL_PROXY_CDN_URL = "/cdn-data/";

// =============================================================================
// Environment Configuration
// =============================================================================

/**
 * Build the full URL for a given environment
 */
const buildUrl = (baseUrl: string, environment?: string): string => {
  return environment ? `${baseUrl}${environment}/` : baseUrl;
};

/**
 * Configuration for each environment
 */
const ENVIRONMENT_CONFIG = {
  LOCAL: {
    test: securSpacePlatforms.LOCAL.hostnameTest,
    useCache: LOCAL_TESTING_MODE,
    url: buildUrl(LOCAL_PROXY_CDN_URL, securSpacePlatforms.TEST.name),
  },
  TEST: {
    test: securSpacePlatforms.TEST.hostnameTest,
    useCache: true,
    url: buildUrl(CLOUDFRONT_CDN_URL, securSpacePlatforms.TEST.name),
  },
  SANDBOX: {
    test: securSpacePlatforms.SANDBOX.hostnameTest,
    useCache: true,
    url: buildUrl(CLOUDFRONT_CDN_URL, securSpacePlatforms.SANDBOX.name),
  },
  PRODUCTION: {
    test: securSpacePlatforms.PRODUCTION.hostnameTest,
    useCache: true,
    url: CLOUDFRONT_CDN_URL,
  },
} as const;

// =============================================================================
// TypeScript Interfaces
// =============================================================================

/**
 * Structure of cached location data from CDN matching the locations.json format
 */
interface CachedLocationData {
  id: string;
  lat: number;
  lon: number;
  locationName: string;
  city: string;
  state: string;
  active: boolean;
  lastUpdated: string;
  pricePerDay?: number;
  pricePerMonth?: number;
  managedSpaces: number;
  totalSpacesBooked: number;
  hasInstantApproval: boolean;
  listingImageFileName: string;
  gallery: string[];
  features: string[];
  /** Feature sub-details keyed by feature name; absent until the cache publishes them. */
  featureDetails?: LocationFeatureDetailsByName | null;
  equipmentTypes: string[];
  /** Raw resolved capacity for today from the effective-dated capacity schedule; null when unlimited/not schedule-tracked. */
  effectiveCapacityToday?: number | null;
  /** Whether the location has any available capacity today. Null capacity is always treated as available (true). */
  hasAvailableCapacityToday?: boolean;
  /** Date the location is expected to reopen once at zero capacity; null when not currently at zero or no future row exists. */
  nextAvailableDate?: string | null;
}

/**
 * Response structure from the cached locations endpoint
 */
interface CachedLocationsResponse {
  locations: CachedLocationData[];
}

/**
 * Superagent response type for cached locations
 */
type CachedLocationsSuperagentResponse = Response & {
  body: CachedLocationsResponse;
};

// =============================================================================
// In-Memory Cache
// =============================================================================

let cachedLocationsUrl: string | null = null;
let cachedLocationsData: CachedLocationsSuperagentResponse | null = null;
let cachedLocationsPromise: Promise<CachedLocationsSuperagentResponse> | null = null;

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the environment configuration for the current hostname
 * @param hostname - The current hostname
 * @returns Environment configuration (defaults to PRODUCTION if no match)
 */
const getEnvironmentConfig = (hostname: string) => {
  return Object.values(ENVIRONMENT_CONFIG).find(config => config.test.test(hostname))
    || ENVIRONMENT_CONFIG.PRODUCTION;
};

/**
 * Determine if cached supplier locations should be used based on the hostname
 * @param hostname - The current hostname
 * @returns True if cache should be used, false otherwise
 */
const shouldUseCache = (hostname: string): boolean => {
  return getEnvironmentConfig(hostname).useCache;
};

/**
 * Get the appropriate filename based on user permissions
 * @param user - The current user account
 * @returns Either 'locations.json' or 'all_locations.json' based on admin access
 */
const getFileName = (user: Account | null | undefined): string => {
  return hasAdminAccess(user) ? FILE_NAMES.ADMIN : FILE_NAMES.STANDARD;
};

/**
 * Build the complete URL for fetching cached locations
 * @param user - The current user account
 * @param hostname - The current hostname
 * @returns The full URL to fetch cached locations from
 */
const getLocationsUrl = (user: Account | null | undefined, hostname: string): string => {
  const config = getEnvironmentConfig(hostname);
  return `${config.url}${getFileName(user)}`;
};

/**
 * Fetch cached supplier locations from the appropriate s3 URL.
 * Keeps the file in memory and reuses it until the URL changes or a caller forces a refresh.
 * @param options.forceRefresh - Drop the in-memory copy and fetch the file again.
 * @returns {Promise<CachedLocationsSuperagentResponse>} - A promise that resolves to the fetched locations data.
 */
const getCachedLocationSuppliers = (
  user: Account | null | undefined,
  hostname: string,
  options: { forceRefresh?: boolean } = {}
): Promise<CachedLocationsSuperagentResponse> => {
  const url = getLocationsUrl(user, hostname);
  if (options.forceRefresh || cachedLocationsUrl !== url) {
    // forced refresh or the desired cache filepath has changed, clear existing cache and fetch new data
    clearCachedLocations();
  } else if (cachedLocationsData) {
    // Return cached data if already loaded
    return Promise.resolve(cachedLocationsData);
  } else if (cachedLocationsPromise) {
    // If a fetch is already in progress, return that promise to avoid duplicate requests
    return cachedLocationsPromise;
  }

  // Fetch the data and cache both the promise and the result
  cachedLocationsUrl = url;
  const pending: Promise<CachedLocationsSuperagentResponse> = request.get(cachedLocationsUrl)
    .then((response: CachedLocationsSuperagentResponse) => {
      // A refresh may have started a newer fetch; never let an older one overwrite it
      if (cachedLocationsPromise === pending) {
        cachedLocationsData = response;
      }
      return response;
    })
    .finally(() => {
      // Clear the promise after completion (success or error) to allow retry
      if (cachedLocationsPromise === pending) {
        cachedLocationsPromise = null;
      }
    });
  cachedLocationsPromise = pending;

  return pending;
}

/**
 * Clear the in-memory cache. Useful for testing or forcing a refresh.
 */
const clearCachedLocations = (): void => {
  cachedLocationsUrl = null;
  cachedLocationsData = null;
  cachedLocationsPromise = null;
}

export {
  shouldUseCache,
  getCachedLocationSuppliers,
  clearCachedLocations
}
